import { useEffect, useState } from "react";
import api from "../api/axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { getToken } from "../auth/auth";
import AlertBox from "../components/AlertBox";
import CardProductGrid from "../components/CardProductGrid";
import { useTranslation } from "react-i18next"; // <-- 1. استدعاء الترجمة

const API_BASE = "https://store.wageh.net/blog";

export default function Products() {
    const nav = useNavigate();
    const token = getToken();
    const [searchParams] = useSearchParams();
    const { t } = useTranslation(); // <-- 2. تعريف الترجمة

    const [products, setProducts] = useState([]);
    const [meta, setMeta] = useState(null);
    const [categories, setCategories] = useState([]);

    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [msg, setMsg] = useState("");

    const [search, setSearch] = useState(searchParams.get("search") || "");
    const [categoryId, setCategoryId] = useState("");
    const [minPrice, setMinPrice] = useState("");
    const [maxPrice, setMaxPrice] = useState("");
    const [sort, setSort] = useState("");

    const buildQuery = (page = 1, searchQuery = search, sortQuery = sort) => {
        const params = new URLSearchParams();
        params.set("page", String(page));
        if (searchQuery.trim()) params.set("search", searchQuery.trim());
        if (categoryId) params.set("category_id", categoryId);
        if (minPrice) params.set("min_price", minPrice);
        if (maxPrice) params.set("max_price", maxPrice);
        if (sortQuery) params.set("sort", sortQuery);
        return params.toString();
    };

    const fetchCategories = async () => {
        try {
            const res = await api.get("/categories");
            const payload = res.data.data ?? res.data;
            setCategories(payload.data ?? payload);
        } catch { }
    };

    const fetchProducts = async (page = 1, searchQuery = search, sortQuery = sort) => {
        setLoading(true);
        setErr("");
        try {
            const qs = buildQuery(page, searchQuery, sortQuery);
            const res = await api.get(`/products?${qs}`);
            const result = res.data.data || res.data;
            setProducts(Array.isArray(result) ? result : []);
            setMeta(res.data.meta || null);
        } catch (e) {
            setErr(e.response?.data?.message || t("products_load_error"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        const queryWord = searchParams.get("search");
        if (queryWord !== null) {
            setSearch(queryWord);
            fetchProducts(1, queryWord, sort);
        } else {
            fetchProducts(1, search, sort);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const applyFilters = (e) => {
        e.preventDefault();
        fetchProducts(1, search, sort);
    };

    const resetFilters = () => {
        setSearch("");
        setCategoryId("");
        setMinPrice("");
        setMaxPrice("");
        setSort("");
        nav("/products");
        fetchProducts(1, "", "");
    };

    const handleSortChange = (e) => {
        const newSort = e.target.value;
        setSort(newSort);
        fetchProducts(1, search, newSort);
    };

    const addToCart = async (productId) => {
        setErr("");
        setMsg("");
        if (!token) {
            nav("/login");
            return;
        }
        try {
            await api.post("/cart/items", { product_id: productId, qty: 1 });
            setMsg(t("added_to_cart_success"));
        } catch (e) {
            setErr(e.response?.data?.message || t("add_to_cart_failed"));
        }
    };

    return (
        <div className="container-fluid mt-4 mb-5 px-4">
            <div className="row mb-3">
                <div className="col-12">
                    <AlertBox type="success" message={msg} onClose={() => setMsg("")} />
                    <AlertBox type="danger" message={err} onClose={() => setErr("")} />
                </div>
            </div>

            <div className="row">
                <div className="col-md-3 mb-4">
                    <div className="card shadow-sm border-0">
                        <div className="card-header bg-primary text-white fw-bold py-3">
                            <i className="bi bi-funnel-fill me-2"></i> {t("filter_products")}
                        </div>
                        <div className="card-body bg-light">
                            <form onSubmit={applyFilters}>
                                <div className="mb-3">
                                    <label className="form-label fw-bold small text-muted">{t("search_by_name")}</label>
                                    <input className="form-control" placeholder={t("search_placeholder")} value={search} onChange={(e) => setSearch(e.target.value)} />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-bold small text-muted">{t("category_label")}</label>
                                    <select className="form-select" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                                        <option value="">{t("all_categories")}</option>
                                        {categories.map((c) => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="mb-4">
                                    <label className="form-label fw-bold small text-muted">{t("price_range")}</label>
                                    <div className="row g-2">
                                        <div className="col-6">
                                            <input className="form-control" type="number" placeholder={t("from")} value={minPrice} onChange={(e) => setMinPrice(e.target.value)} />
                                        </div>
                                        <div className="col-6">
                                            <input className="form-control" type="number" placeholder={t("to")} value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} />
                                        </div>
                                    </div>
                                </div>

                                <div className="d-grid gap-2">
                                    <button className="btn btn-primary fw-bold" type="submit">{t("apply_filters")}</button>
                                    <button className="btn btn-outline-secondary" type="button" onClick={resetFilters}>{t("reset_filters")}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                <div className="col-md-9">
                    <div className="row mb-3 align-items-center bg-white p-3 shadow-sm rounded border-0 mx-0">
                        <div className="col-md-6 mb-2 mb-md-0">
                            <span className="align-middle fw-bold text-dark">
                                {t("showing_available_products")}
                                {meta && <span className="text-muted mx-2 small">({meta.total} {t("products_count")})</span>}
                            </span>
                        </div>
                        <div className="col-md-6 d-flex justify-content-md-end">
                            <div className="d-flex align-items-center">
                                <label className="mx-2 fw-bold small text-muted text-nowrap">{t("sort_by")}</label>
                                <select className="form-select form-select-sm border-primary" style={{ width: "200px" }} value={sort} onChange={handleSortChange}>
                                    <option value="">{t("sort_latest")}</option>
                                    <option value="price_asc">{t("sort_price_asc")}</option>
                                    <option value="price_desc">{t("sort_price_desc")}</option>
                                    <option value="name_asc">{t("sort_name_asc")}</option>
                                    <option value="name_desc">{t("sort_name_desc")}</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center py-5 my-5">
                            <div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }} role="status" />
                            <p className="mt-3 text-muted fw-bold">{t("loading_products")}</p>
                        </div>
                    ) : products.length === 0 ? (
                        <div className="alert alert-warning text-center py-4 shadow-sm">
                            <i className="bi bi-exclamation-circle display-4 d-block mb-3"></i>
                            {t("no_products_found")}
                        </div>
                    ) : (
                        <>
                            <div className="row g-4">
                                {products.map((p) => (
                                    <div className="col-12 col-sm-6 col-lg-4" key={p.id}>
                                        <CardProductGrid product={p} onAddToCart={addToCart} />
                                    </div>
                                ))}
                            </div>

                            {meta && meta.last_page > 1 && (
                                <div className="d-flex justify-content-center mt-5">
                                    <div className="btn-group shadow-sm" dir="ltr">
                                        <button
                                            className="btn btn-outline-primary px-4"
                                            disabled={meta.current_page <= 1}
                                            onClick={() => {
                                                fetchProducts(meta.current_page - 1, search, sort);
                                                window.scrollTo({ top: 0, behavior: "smooth" });
                                            }}
                                        >
                                            {t("prev_page")}
                                        </button>
                                        <span className="btn btn-primary fw-bold" style={{ pointerEvents: "none" }}>
                                            {t("page_x_of_y", { current: meta.current_page, last: meta.last_page })}
                                        </span>
                                        <button
                                            className="btn btn-outline-primary px-4"
                                            disabled={meta.current_page >= meta.last_page}
                                            onClick={() => {
                                                fetchProducts(meta.current_page + 1, search, sort);
                                                window.scrollTo({ top: 0, behavior: "smooth" });
                                            }}
                                        >
                                            {t("next_page")}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}